import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { makeStyles, TableCell } from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import * as React from "react";
import {
  AutoSizer,
  Column,
  Index,
  RowMouseEventHandlerParams,
  SortDirectionType,
  SortIndicator,
  Table as VirtualizedTable,
  TableCellRenderer,
  TableHeaderRenderer,
} from "react-virtualized";

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 56;

const useStyles = makeStyles(() => ({
  tableWrapper: {
    height: "100%",
  },
  flexContainer: {
    display: "flex",
    alignItems: "center",
  },
  tableHeader: {
    backgroundColor: "#F4F4F4",
    fontWeight: 700,
  },
  tableHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 15,
  },
  tableRow: {
    cursor: "pointer",
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
  },
  tableRowHover: {
    "&:hover": {
      boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.2)",
    },
  },
  tableCell: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
  },
  noClick: {
    cursor: "initial",
  },
}));

interface TableProps {
  columns: {
    width: number;
    fixedWidth?: boolean;
    label: string;
    dataKey: string;
  }[];
  renderBodyCell: TableCellRenderer;
  renderHeaderCell: TableHeaderRenderer;
  rowGetter: (index: Index) => unknown;
  totalRows: number;
  height?: number;
  onClickRow?: (params: RowMouseEventHandlerParams) => void;
  sort?: (params: { sortBy: string; sortDirection: SortDirectionType }) => void;
  sortBy?: string;
  sortDirection?: SortDirectionType;
}

function Table({
  columns,
  renderBodyCell,
  renderHeaderCell,
  rowGetter,
  totalRows,
  height = 0,
  onClickRow,
  sort,
  sortBy,
  sortDirection,
}: TableProps) {
  const classes = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [calculatedHeight, setHeight] = React.useState(height);

  React.useEffect(() => {
    // when height wasn't specified, we try to fill in the whole
    // remaining height of the parent container.
    // TODO: exclude the height that might be used by siblings after table
    if (
      height > 0 ||
      !containerRef.current ||
      !containerRef.current.parentElement
    ) {
      return;
    }

    const container = containerRef.current;

    const handleSetHeight = () =>
      container.parentElement &&
      setHeight(container.parentElement.clientHeight || 0);

    handleSetHeight();

    const ro = new ResizeObserver(handleSetHeight);

    ro.observe(container);

    return () => {
      ro.unobserve(container);
    };
  }, [height]);

  const getRowClassName = ({ index }: { index: number }) =>
    clsx(
      classes.tableRow,
      classes.flexContainer,
      index < 0 ? classes.tableHeaderRow : classes.tableRowHover
    );

  const cellRenderer: TableCellRenderer = (cellData) => (
    <TableCell
      component="div"
      variant="body"
      style={{ height: ROW_HEIGHT }}
      className={clsx(classes.tableCell, classes.flexContainer)}
    >
      {renderBodyCell(cellData)}
    </TableCell>
  );

  const headerRenderer: TableHeaderRenderer = (data) => (
    <TableCell
      component="div"
      className={clsx(
        classes.tableCell,
        classes.flexContainer,
        data.disableSort && classes.noClick
      )}
      variant="head"
      style={{ height: HEADER_HEIGHT }}
      align="left"
    >
      {renderHeaderCell(data)}
      {data.sortBy === data.dataKey && (
        <SortIndicator key="SortIndicator" sortDirection={sortDirection} />
      )}
    </TableCell>
  );

  const totalWidth = React.useMemo(
    () => _.sumBy(columns, (c) => (c.fixedWidth ? 0 : c.width)),
    [columns]
  );

  const fixedWidth = React.useMemo(
    () => _.sumBy(columns, (c) => (c.fixedWidth ? c.width : 0)),
    [columns]
  );

  const getRelativeWidthForColumn = React.useCallback(
    (tableWidth: number, index: number) =>
      columns[index].fixedWidth
        ? columns[index].width
        : ((tableWidth - fixedWidth) * columns[index].width) / totalWidth,
    [columns, totalWidth, fixedWidth]
  );

  return (
    <div ref={containerRef} className={classes.tableWrapper}>
      <AutoSizer disableHeight>
        {({ width }) => {
          return (
            <VirtualizedTable
              width={width}
              height={calculatedHeight || ROW_HEIGHT * 4}
              headerHeight={HEADER_HEIGHT}
              rowHeight={ROW_HEIGHT}
              rowCount={totalRows}
              gridStyle={{
                direction: "inherit",
                overflow: "unset",
              }}
              rowClassName={getRowClassName}
              headerClassName={classes.tableHeader}
              rowGetter={rowGetter}
              sort={sort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onRowClick={onClickRow}
            >
              {columns.map((col, index) => (
                <Column
                  key={col.dataKey}
                  headerRenderer={headerRenderer}
                  className={classes.flexContainer}
                  cellRenderer={cellRenderer}
                  {...col}
                  width={getRelativeWidthForColumn(width, index)}
                />
              ))}
            </VirtualizedTable>
          );
        }}
      </AutoSizer>
    </div>
  );
}

export default Table;
